<template>
  <div class="document-item-menu" data-testid="document-item-menu">
    <tags-selector :document="doc" trigger-locator="button">
      <button
        ref="tagsSelectorTrigger"
        slot="trigger"
        class="tags-selector-trigger"
      ></button>
    </tags-selector>
    <i-menu
      ref="menuEl"
      :close-on-item-click="false"
      :trigger-align="iMenuTriggerAlignOptions.RIGHT"
      :width="250"
    >
      <i-icon-button
        slot="trigger"
        class="trigger-button"
        data-testid="document-actions-button"
        size="md"
        symbol="medium-more-menu-vertical"
      />

      <!-- Reject -->
      <i-menu-item
        v-if="documentStatusEnabled && !documentHasErrorStates"
        slot="items"
        label="Reject"
        :icon-color="palette['light-error-90']"
        icon="small-solid-delete"
        :checked="doc.review_status === documentStatus.REJECTED"
        :disabled="doc.state !== documentStates.PROCESSED"
        data-testid="i-menu-item"
        @item-click="setDocumentStatus(documentStatus.REJECTED)"
      ></i-menu-item>

      <!-- Accept -->
      <i-menu-item
        v-if="documentStatusEnabled && !documentHasErrorStates"
        slot="items"
        label="Accept"
        :icon-color="palette['light-success-90']"
        icon="small-solid-check"
        :checked="doc.review_status === documentStatus.ACCEPTED"
        :disabled="doc.state !== documentStates.PROCESSED"
        data-testid="i-menu-item"
        @item-click="setDocumentStatus(documentStatus.ACCEPTED)"
      ></i-menu-item>

      <div
        v-if="documentStatusEnabled && !documentHasErrorStates"
        slot="items"
        class="divider"
      ></div>

      <!-- Tags manager -->
      <i-menu-item
        v-if="!documentHasErrorStates"
        slot="items"
        label="Manage Tags"
        icon="small-tag"
        :disabled="doc.state !== documentStates.PROCESSED"
        data-testid="i-menu-item"
        @item-click="openTagsManager()"
      ></i-menu-item>

      <!-- Download -->
      <i-menu-item
        v-if="!documentHasErrorStates"
        slot="items"
        label="Download"
        icon="small-download-button-2"
        :disabled="
          doc.state !== documentStates.PROCESSED || !doc.urls.original_file
        "
        data-testid="i-menu-item"
        @item-click="downloadDocument()"
      ></i-menu-item>

      <div v-if="!documentHasErrorStates" slot="items" class="divider"></div>

      <!-- delete -->
      <i-menu-item
        v-if="doc.state !== documentStates.ERROR_UPLOADING"
        slot="items"
        label="Delete"
        icon="small-delete-bin-1"
        :icon-color="palette['light-error-110']"
        :text-color="palette['light-error-110']"
        data-testid="i-menu-item"
        @item-click="tryDeleteDocument()"
      ></i-menu-item>

      <!-- remove -->
      <i-menu-item
        v-if="doc.state === documentStates.ERROR_UPLOADING"
        slot="items"
        label="Remove"
        icon="small-delete-bin-1"
        :icon-color="palette['light-error-110']"
        :text-color="palette['light-error-110']"
        data-testid="i-menu-item"
        @item-click="removeUpload()"
      ></i-menu-item>
    </i-menu>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import {
  iIconButton,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
} from '@inscribe/signal';
import * as documentStates from '@/constants/documentStates';
import * as documentStatus from '@/constants/documentStatus';
import palette from '@inscribe/signal/dist/palette';
import { downloadDocument } from '@/utils/download';
import TagsSelector from '@/V2/components/TagsSelector.vue';

export default {
  components: {
    iIconButton,
    iMenu,
    iMenuItem,
    TagsSelector,
  },
  props: {
    doc: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      iMenuTriggerAlignOptions,
      documentStates,
      documentStatus,
      palette,
    };
  },
  computed: {
    ...mapGetters({
      documentStatusEnabled: 'documentStatusEnabled',
    }),

    documentHasErrorStates() {
      return [
        documentStates.ERROR_PROCESSING,
        documentStates.ERROR_UPLOADING,
      ].includes(this.doc.state);
    },
  },
  methods: {
    ...mapMutations(['SET_SELECTED_FILTER', 'SET_FILTER_PAGE']),

    ...mapActions({
      deleteDocument: 'deleteDocument',
      getFilteredDocumentsForCustomer: 'getFilteredDocumentsForCustomer',
      resetFilteredState: 'resetFilteredState',
      trySetDocumentStatus: 'trySetDocumentStatus',
    }),
    setDocumentStatus(status) {
      this.trySetDocumentStatus({
        document: this.doc,
        data: { review_status: status },
      })
        .then((currDoc) => {
          const textsMap = {};
          textsMap[documentStatus.ACCEPTED] = 'Accepted';
          textsMap[documentStatus.REJECTED] = 'Rejected';
          textsMap[documentStatus.REQUIRES_REVIEW] = 'Requires Review';
          this.$notify({
            group: 'api',
            type: 'success',
            duration: 5000,
            text: `${this.doc?.name} status changed to ${
              textsMap[currDoc.review_status]
            }`,
          });
        })
        .catch((err) => {
          this.$notify({
            group: 'api',
            type: 'error',
            duration: 5000,
            text: err.response,
          });
        });
    },
    downloadDocument() {
      this.isDownloading = true;
      downloadDocument(this.doc.urls.original_file, this.doc.name).then(() => {
        this.isDownloading = false;
      });
    },
    tryDeleteDocument() {
      const docData = {
        customerId: this.doc.customer_id,
        document: this.doc,
      };
      this.deleteDocument(docData);
      this.$emit('document-deleted', docData);
      this.closeMenu();
    },
    removeUpload() {
      const documents = this.$store.state.documents.filter(
        (currDoc) => currDoc.uploadId !== this.doc.uploadId,
      );
      this.$store.commit('setDocuments', documents);
    },
    openTagsManager() {
      this.$refs.tagsSelectorTrigger.click();
      this.closeMenu();
    },
    closeMenu() {
      this.$refs.menuEl?.setMenuOpen(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.document-item-menu {
  position: relative;

  :deep(.tags-selector) {
    height: 0;
    width: 0;

    .tags-selector-trigger {
      padding: 0;
      border: 0;
    }
  }

  .trigger-button {
    :deep(.i-icon) {
      stroke-width: 0 !important;
    }
  }

  .tags-dropdown-container {
    position: absolute !important;
    top: 0;

    .tags-button {
      display: none;
    }
  }
}
</style>
